import { useState } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { RevealItem, ScrollReveal } from '#/components/common/ScrollReveal'
import { TextAnimate } from '#/components/common/TextAnimate'
import { HighlightText } from '#/components/common/HighlightText'
import headerIcon from '#/assets/header-icon.svg'
import { FAQ_CONTENT, FAQ_ITEMS } from '#/data/aboutFaq'
import { useFaqs } from '#/features/faqs/hooks/useFaqs'
import { LuPlus, LuMinus } from 'react-icons/lu'

/* ── Crescent toggle icon ─────────────────────────────────────────────── */
function CrescentIcon({ open }: { open: boolean }) {
  return (
    <div
      className={`flex size-9 flex-shrink-0 items-center justify-center rounded-full border transition-all duration-300 ${
        open
          ? 'border-primary-500 bg-primary-500 text-neutral-900'
          : 'border-neutral-200 bg-white text-secondary-500'
      }`}
    >
      {open ? (
        <LuMinus className="text-base" />
      ) : (
        <LuPlus className="text-base" />
      )}
    </div>
  )
}

/* ── Islamic arch left-side accent ───────────────────────────────────── */
function IslamicArchAccent() {
  return (
    <svg
      className="pointer-events-none absolute left-0 top-0 h-full w-40 opacity-[0.04] max-lg:hidden"
      viewBox="0 0 160 800"
      preserveAspectRatio="none"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      {/* Repeating arch pattern */}
      {[0, 120, 240, 360, 480, 600].map((y) => (
        <path
          key={y}
          d={`M 20 ${y + 100} Q 80 ${y} 140 ${y + 100} L 140 ${y + 120} Q 80 ${y + 20} 20 ${y + 120} Z`}
          stroke="#FFB606"
          strokeWidth="1"
          fill="none"
        />
      ))}
      {/* Connecting dots */}
      {[60, 180, 300, 420, 540, 660].map((y) => (
        <circle key={y} cx="80" cy={y} r="3" fill="#FFB606" />
      ))}
    </svg>
  )
}

/* ── FAQ Accordion Item ───────────────────────────────────────────────── */
function FaqItem({
  question,
  answer,
  isOpen,
  onClick,
  index,
}: {
  question: string
  answer: string
  isOpen: boolean
  onClick: () => void
  index: number
}) {
  return (
    <RevealItem>
      <div
        className={`overflow-hidden rounded-xl border transition-all duration-300 ${
          isOpen
            ? 'border-primary-500/30 shadow-[0_4px_20px_rgba(255,182,6,0.08)]'
            : 'border-neutral-200 hover:border-neutral-300'
        }`}
      >
        <button
          type="button"
          id={`faq-button-${index}`}
          aria-expanded={isOpen}
          aria-controls={`faq-panel-${index}`}
          onClick={onClick}
          className="flex w-full items-center justify-between gap-4 bg-white px-6 py-5 text-left transition-colors hover:bg-neutral-50"
        >
          <div className="flex items-center gap-4">
            {/* Number */}
            <span className="text-3xl font-bold text-neutral-100 select-none min-w-[2.5rem]">
              {String(index + 1).padStart(2, '0')}
            </span>
            <span className="text-base font-semibold text-neutral-900 sm:text-lg">
              {question}
            </span>
          </div>
          <CrescentIcon open={isOpen} />
        </button>

        <AnimatePresence initial={false}>
          {isOpen && (
            <motion.div
              id={`faq-panel-${index}`}
              role="region"
              aria-labelledby={`faq-button-${index}`}
              key="content"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden bg-white"
            >
              <div className="border-t border-neutral-100 px-6 pb-6 pt-4 pl-[calc(1.5rem+2.5rem+1rem)]">
                <p className="text-base leading-relaxed text-neutral-500">
                  {answer}
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </RevealItem>
  )
}

/* ── Component ───────────────────────────────────────────────────────── */
export default function FaqSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0)
  const { data: apiFaqs } = useFaqs()

  const items =
    apiFaqs && apiFaqs.length > 0
      ? apiFaqs.map((f) => ({ question: f.question, answer: f.answer }))
      : FAQ_ITEMS

  const toggle = (index: number) => {
    setOpenIndex((prev) => (prev === index ? null : index))
  }

  return (
    <section
      className="relative w-full py-20 sm:py-28 overflow-hidden bg-background scroll-mt-20"
      id="faqs"
    >
      <IslamicArchAccent />

      {/* Subtle gradient top */}
      <div
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            'radial-gradient(60% 40% at 80% 20%, rgba(255,182,6,0.05) 0%, transparent 70%)',
        }}
      />

      <div className="container mx-auto flex flex-col gap-16">
        {/* Header */}
        <ScrollReveal className="flex flex-col items-center gap-4 text-center">
          <RevealItem>
            <img alt="" src={headerIcon} />
          </RevealItem>
          <RevealItem>
            <p className="text-sm font-semibold uppercase tracking-[0.3em] text-primary-700">
              {FAQ_CONTENT.eyebrow}
            </p>
          </RevealItem>
          <RevealItem className="w-full">
            <h2 className="text-4xl font-bold text-neutral-900 sm:text-5xl">
              {FAQ_CONTENT.title}{' '}
              <HighlightText
                inView
                text={FAQ_CONTENT.highlight}
                className="bg-linear-to-r from-primary-100/70 to-primary-300/70 text-neutral-900"
              />
            </h2>
          </RevealItem>
          <RevealItem className="max-w-2xl">
            <TextAnimate
              by="word"
              animation="fadeIn"
              duration={1.1}
              className="text-base text-neutral-500 sm:text-lg"
            >
              {FAQ_CONTENT.description}
            </TextAnimate>
          </RevealItem>
        </ScrollReveal>

        {/* Two-column FAQ grid */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Left column */}
          <ScrollReveal
            key={`faq-left-${apiFaqs ? 'loaded' : 'initial'}`}
            className="flex flex-col gap-4"
            stagger={0.1}
          >
            {items.slice(0, Math.ceil(items.length / 2)).map((item, idx) => (
              <FaqItem
                key={item.question}
                question={item.question}
                answer={item.answer}
                isOpen={openIndex === idx}
                onClick={() => toggle(idx)}
                index={idx}
              />
            ))}
          </ScrollReveal>

          {/* Right column */}
          <ScrollReveal
            key={`faq-right-${apiFaqs ? 'loaded' : 'initial'}`}
            className="flex flex-col gap-4"
            stagger={0.1}
          >
            {items.slice(Math.ceil(items.length / 2)).map((item, idx) => {
              const actualIdx = idx + Math.ceil(items.length / 2)
              return (
                <FaqItem
                  key={item.question}
                  question={item.question}
                  answer={item.answer}
                  isOpen={openIndex === actualIdx}
                  onClick={() => toggle(actualIdx)}
                  index={actualIdx}
                />
              )
            })}
          </ScrollReveal>
        </div>

        {/* CTA block */}
        <ScrollReveal className="flex justify-center">
          <RevealItem>
            <div className="flex flex-col items-center gap-4 rounded-2xl border border-primary-200 bg-white px-8 py-8 text-center shadow-sm max-w-xl">
              <div className="text-4xl">🕌</div>
              <h3 className="text-xl font-bold text-neutral-900">
                Still have questions?
              </h3>
              <p className="text-base text-neutral-500">
                Our team is ready to help. Reach out and we'll respond within 24
                hours.
              </p>
              <a
                href="/contact"
                className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-neutral-900 transition-all hover:bg-primary-400 hover:shadow-lg hover:shadow-primary-500/20"
              >
                Contact Us
              </a>
            </div>
          </RevealItem>
        </ScrollReveal>
      </div>
    </section>
  )
}
